import { useCallback, useEffect, useState } from 'react';
import { getSettings, listActivity, updateSettings, type ActivityItem } from '../utils/apiClient';

interface UseActivity {
  items: ActivityItem[];
  unreadCount: number;
  lastReadAt: string | null;
  markAllRead: () => Promise<void>;
  refresh: () => Promise<void>;
}

const POLL_MS = 30_000;

export function useActivity(): UseActivity {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [lastReadAt, setLastReadAt] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [activity, settings] = await Promise.all([
        listActivity(30),
        getSettings().catch(() => null),
      ]);
      setItems(activity);
      if (settings?.activityLastReadAt) setLastReadAt(settings.activityLastReadAt);
    } catch {
      /* swallow; next tick retries */
    }
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => void refresh(), POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    setLastReadAt(now);
    try {
      await updateSettings({ activityLastReadAt: now });
    } catch {
      /* local state wins; server will sync on next update */
    }
  }, []);

  const unreadCount = lastReadAt
    ? items.filter((it) => it.createdAt > lastReadAt).length
    : items.length;

  return { items, unreadCount, lastReadAt, markAllRead, refresh };
}
